<template>
  <Callout.Container edge="bottom">
    <Callout.Root v-if="IS_IMPERSONATED" variant="warning">
      Logged in as this user.

      <template #actions>
        <Callout.Action href="/admin/logout/">Return to admin</Callout.Action>
      </template>
    </Callout.Root>

    <Callout.Root
      v-if="
        INCIDENT_MESSAGE_VALUE && INCIDENT_MESSAGE_VALUE !== 'INCIDENT_MESSAGE'
      "
      variant="error"
    >
      {{ INCIDENT_MESSAGE_VALUE }}

      <template #actions>
        <Callout.Action href="https://status.buttondown.com">
          Status page
        </Callout.Action>
      </template>
    </Callout.Root>

    <Callout.Root
      v-if="newsletter.sending_domain_status === 'invalid'"
      variant="warning"
    >
      Finish setting up your sending domain to send emails.

      <template #actions>
        <Callout.Action :to="{ name: '/settings/domains.sending' }">
          Learn more
        </Callout.Action>
      </template>
    </Callout.Root>

    <Callout.Root
      v-if="
        newsletter.email_domain !== '' &&
        newsletter.email_address &&
        !newsletter.email_address.endsWith(`@${newsletter.email_domain}`)
      "
      variant="error"
    >
      Your email address doesn't match sending domain — emails will fail to
      send.
    </Callout.Root>

    <Callout.Root
      v-if="account.billing_plan_status === 'past_due'"
      variant="error"
    >
      <template #title>Account past due.</template>

      Update your payment info to resume sending.

      <template #actions>
        <Callout.Action href="/settings/billing">
          Update payment info
        </Callout.Action>
      </template>
    </Callout.Root>

    <Callout.Root
      v-if="newsletter.paid_subscriptions_status === 'disabled'"
      variant="error"
    >
      Stripe has disabled paid subscriptions for this newsletter.

      <template #actions>
        <Callout.Action href="https://dashboard.stripe.com/account/status">
          Go to Stripe
        </Callout.Action>
      </template>
    </Callout.Root>
    <Callout.Root
      v-else-if="newsletter.paid_subscriptions_status === 'disconnected'"
      variant="error"
    >
      Stripe account disconnected.

      <template #actions>
        <Callout.Action :to="{ name: '/settings/paid-subscriptions' }">
          Reconnect Stripe
        </Callout.Action>
      </template>
    </Callout.Root>

    <Callout.Root v-if="newsletter.is_test_mode_enabled" variant="warning">
      Test mode enabled — all emails go to you.
      <template v-if="route.name !== '/settings/danger-zone'" #actions>
        <Callout.Action :to="{ name: '/settings/danger-zone' }">
          Disable test mode
        </Callout.Action>
      </template>
    </Callout.Root>

    <Callout.Root v-if="newsletter.status === 'undeliverable'" variant="error">
      Can't reach your email address — contact support@buttondown.com to resolve
      this issue.
    </Callout.Root>
    <template v-else-if="newsletter.status === 'permanently_disabled'">
      <Callout.Root v-if="!hasSubmittedKyc(newsletter)" variant="error">
        Your account has been paused for a human review. Please provide some
        information so we can verify your account.

        <template v-if="route.name !== '/home'" #actions>
          <Callout.Action :to="{ name: '/home' }">
            Provide information
          </Callout.Action>
        </template>
      </Callout.Root>
      <Callout.Root v-else variant="error">
        We're unable to approve your account. Please contact us if you have any
        questions.
        <template #actions>
          <Callout.Action href="mailto:support@buttondown.com">
            Contact us
          </Callout.Action>
        </template>
      </Callout.Root>
    </template>
    <template v-else-if="newsletter.status === 'disabled'">
      <Callout.Root v-if="!hasSubmittedKyc(newsletter)" variant="warning">
        Your account has been paused for a human review. Please provide some
        information so we can verify your account.

        <template v-if="route.name !== '/home'" #actions>
          <Callout.Action :to="{ name: '/home' }">
            Provide information
          </Callout.Action>
        </template>
      </Callout.Root>
      <Callout.Root v-else variant="warning">
        Thanks for the info — a real person is reviewing your account. This
        usually takes a few hours.

        <template #actions>
          <Callout.Action href="https://docs.buttondown.com/account-review">
            Learn more
          </Callout.Action>
        </template>
      </Callout.Root>
    </template>
    <Callout.Root
      v-else-if="newsletter.status === 'unconfirmed'"
      variant="info"
    >
      Confirm your email address so subscribers can confirm their subscription.

      <template #actions>
        <Callout.Action @click="resend">Resend</Callout.Action>
      </template>
    </Callout.Root>
    <Callout.Root v-else-if="newsletter.status === 'dormant'" variant="info">
      This newsletter is marked dormant.

      <template #actions>
        <Callout.Action :to="{ name: '/settings/danger-zone' }">
          Re-enable newsletter
        </Callout.Action>
      </template>
    </Callout.Root>

    <Callout.Root v-if="newsletter.deletion_date" variant="warning">
      This newsletter is scheduled for deletion on
      {{ formattedDeletionDate }}.

      <template #actions>
        <Callout.Action :to="{ name: '/settings/danger-zone' }">
          Cancel deletion
        </Callout.Action>
      </template>
    </Callout.Root>

    <Callout.Root
      v-if="(disabledWebhookCount ?? 0) > 0 && route.name !== '/webhooks/[id]'"
      variant="warning"
    >
      <template v-if="disabledWebhookCount === 1">
        A webhook is disabled — no events are being delivered.
      </template>
      <template v-else>
        {{ disabledWebhookCount }} webhooks are disabled — no events are being
        delivered.
      </template>

      <template #actions>
        <Callout.Action :to="{ name: '/webhooks' }"
          >View webhooks</Callout.Action
        >
      </template>
    </Callout.Root>
  </Callout.Container>

  <TooltipProvider :delay-duration="1000" ignore-non-keyboard-focus>
    <DrawerPortal.Root
      :key="newsletter.id"
      v-slot="{ open: isDrawerOpen, variant: drawerVariant }"
    >
      <div
        id="app-inner"
        class="relative z-0 flex h-dvh min-w-0 overflow-hidden bg-white transition dark:bg-background dark:text-white"
        :style="{ '--tint-color': newsletter.tint_color || '#0069FF' }"
      >
        <Aside @search="isSearchOpen = true" />

        <div
          class="z-0 flex min-w-0 grow flex-col transition-all duration-300 ease-bounce @container/panel motion-reduce:transition-none"
          :inert="isMobileSidebarOpen || (isDrawerOpen && isMobile)"
          :class="[isWidescreen && isDesktopSidebarOpen && 'ml-52']"
          :style="panelOffsetStyle(isDrawerOpen, drawerVariant)"
        >
          <RouterView />
        </div>

        <DrawerRenderer />
      </div>
    </DrawerPortal.Root>

    <EmphasisOverlay />

    <BulkActionProvider />

    <ErrorModal v-for="error in errors" :key="error.detail" :error="error" />

    <SearchModal v-if="isSearchOpen" @close="isSearchOpen = false" />
    <ShortcutRenderer />
    <ToastRenderer />
  </TooltipProvider>
</template>

<script setup lang="ts">
import { TooltipProvider } from "reka-ui";
import { type CSSProperties, computed, ref, toRef, watch } from "vue";
import { useRoute } from "vue-router";

import URLS from "@/autogen/urls";
import BulkActionProvider from "@/components/bulk-actions/BulkActionProvider.vue";
import Callout from "@/design_system/Callout";
import {
  drawerOffsetStyle,
  useReservesDrawerSpace,
} from "@/design_system/Drawer/layout";
import * as DrawerPortal from "@/design_system/Drawer/Portal";
import { useXsrfHeaders } from "@/lib/api/queries/utils";
import { useWebhooksListingQuery } from "@/lib/api/queries/webhooks/list";
import { useMediaQuery } from "@/lib/composables/media-query";
import { IS_IMPERSONATED } from "@/lib/environment";
import { useKeybinds } from "@/lib/hotkeys";
import { showToast } from "@/lib/toasts";
import { useAccount, useNewsletter } from "@/store/stores/account";
import { useStore as useErrorsStore } from "@/store/stores/errors";
import { useStore as useUiStatesStore } from "@/store/stores/ui-states";
import type { Newsletter } from "@/types/newsletter";

const hasSubmittedKyc = (newsletter: Newsletter): boolean => {
  const onboardingData = newsletter.onboarding_data as Record<string, unknown>;
  return onboardingData?.kyc_submitted === true;
};

import { Aside } from "./Aside";
import DrawerRenderer from "./DrawerRenderer.vue";
import EmphasisOverlay from "./EmphasisOverlay.vue";
import ErrorModal from "./ErrorModal.vue";
import SearchModal from "./SearchModal/Component.vue";
import ShortcutRenderer from "./ShortcutRenderer.vue";
import ToastRenderer from "./ToastRenderer.vue";

window.document.title = "Buttondown";

// Expose INCIDENT_MESSAGE to template
// Read from window.INCIDENT_MESSAGE (set by HTML script tag or test setup)
// Vite's define will replace this at compile time if INCIDENT_MESSAGE is defined
const INCIDENT_MESSAGE_VALUE =
  typeof window !== "undefined" ? (window as any).INCIDENT_MESSAGE || "" : "";

const route = useRoute();

const errorsStore = useErrorsStore();
const uiStates = useUiStatesStore();

const hideSidebarParam = computed(() => {
  const hideSidebar = route.query.hideSidebar;
  return hideSidebar === "true" || hideSidebar === "1";
});

const isDesktopSidebarOpen = computed(() => {
  return !hideSidebarParam.value && uiStates.isDesktopSidebarOpen;
});

const isMobileSidebarOpen = computed(() => {
  return !hideSidebarParam.value && uiStates.isMobileSidebarOpen;
});

const account = useAccount();
const newsletter = useNewsletter();

const hasWebhooksFeature = computed(
  () => newsletter.value.enabled_features?.includes("webhooks") ?? false
);

const { count: disabledWebhookCount } = useWebhooksListingQuery({
  enabled: hasWebhooksFeature,
  status: "disabled",
});

const formattedDeletionDate = computed(() => {
  if (!newsletter.value.deletion_date) return "";
  return new Date(newsletter.value.deletion_date).toLocaleDateString(
    undefined,
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );
});

const errors = toRef(errorsStore, "resource");

const isMobile = useMediaQuery("(width < 640px)");

const isWidescreen = useMediaQuery("(width >= 1024px)");
const reservesDrawerSpace = useReservesDrawerSpace();

const panelOffsetStyle = (
  isDrawerOpen: boolean,
  drawerVariant: DrawerPortal.DrawerVariant
): CSSProperties => {
  if (!reservesDrawerSpace.value || !isDrawerOpen) {
    return { marginRight: "0px" };
  }
  return drawerOffsetStyle(drawerVariant);
};

const isSearchOpen = ref(false);

watch(
  toRef(uiStates, "appThemeNormalized"),
  (theme) => {
    document.documentElement.classList.toggle(
      "dark",
      theme === "dark" ||
        (window.location.search.includes("dark=true") &&
          !window.location.search.includes("dark=false"))
    );
  },
  { immediate: true }
);

watch([isWidescreen, () => route.path], () => {
  uiStates.isMobileSidebarOpen = false;
});

const pendingResend = ref(false);
const headers = useXsrfHeaders();

const resend = () => {
  pendingResend.value = true;

  try {
    // We deliberately don't await this; we just want to show a toast immediately.
    fetch(URLS["resend-confirmation-email"](), {
      method: "POST",
      headers: {
        ...headers.value,
        accept: "application/json",
        "content-type": "application/json",
      },
    });
  } finally {
    pendingResend.value = false;
  }

  showToast({
    type: "inform",
    title: `Confirmation email sent`,
    message: `Check your inbox to confirm your account.`,
  });
};

useKeybinds({
  "$mod+k": {
    name: `Open quick search`,
    group: `Application`,
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      isSearchOpen.value = true;
      return true;
    },
  },
  "[": {
    name: `Toggle sidebar`,
    group: `Application`,
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      if (isWidescreen.value) {
        uiStates.isDesktopSidebarOpen = !uiStates.isDesktopSidebarOpen;
      } else {
        uiStates.isMobileSidebarOpen = !uiStates.isMobileSidebarOpen;
      }

      return true;
    },
  },
  Escape: {
    name: `Hide sidebar`,
    group: `Application`,
    hidden: true,
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      if (isMobileSidebarOpen.value && !isWidescreen.value) {
        uiStates.isMobileSidebarOpen = false;
        return true;
      }
    },
  },
});
</script>

<style lang="css">
@import "@/styles/base.css";
</style>
